/*
 * Gift wrap quick add for the cart drawer.
 *
 * One checkbox per gift wrap variant: ticking it adds a single unit to the
 * cart, unticking it removes the matching line. Nothing is stored client side,
 * the drawer is re-rendered from the `cart-drawer` section after every change
 * so the boxes always reflect the real cart.
 */
if (!customElements.get('gift-wrap-options')) {
	// Lives outside the class on purpose: the element is thrown away and rebuilt
	// on every drawer render, but the accordion has to stay as the customer left it.
	let isAccordionOpen = false;

	customElements.define(
		'gift-wrap-options',
		class GiftWrapOptions extends HTMLElement {
			constructor() {
				super();

				this.details = this.querySelector('.cart-gift-wrap__details');
				this.errorElement = this.querySelector('.cart-gift-wrap__error');
				this.onChange = this.onChange.bind(this);
				this.onToggle = this.onToggle.bind(this);
			}

			connectedCallback() {
				if (this.details) {
					if (isAccordionOpen) this.details.open = true;
					isAccordionOpen = this.details.open;
					this.details.addEventListener('toggle', this.onToggle);
				}

				this.addEventListener('change', this.onChange);
			}

			disconnectedCallback() {
				this.removeEventListener('change', this.onChange);
				if (this.details) this.details.removeEventListener('toggle', this.onToggle);
			}

			onToggle() {
				isAccordionOpen = this.details.open;
			}

			onChange(event) {
				const checkbox = event.target;
				if (!checkbox.classList.contains('cart-gift-wrap__checkbox')) return;

				this.hideError();

				if (checkbox.checked) {
					// `_gift_wrap` marks the line as ours. Regios auto-adds the same
					// variant from its own rule, and only this property tells the two
					// apart when we later have to remove one.
					this.submit(window.routes.cart_add_url, {
						items: [{ id: Number(checkbox.dataset.variantId), quantity: 1, properties: { _gift_wrap: 'true' } }],
					}, checkbox);
					return;
				}

				// No line key means the cart moved on without us, just resync the drawer.
				if (!checkbox.dataset.lineKey) {
					this.submit(window.routes.cart_update_url, { updates: {} }, checkbox);
					return;
				}

				this.submit(window.routes.cart_change_url, { id: checkbox.dataset.lineKey, quantity: 0 }, checkbox);
			}

			submit(url, payload, checkbox) {
				this.setLoading(true);

				const body = JSON.stringify({
					...payload,
					sections: this.getSectionsToRender().map((section) => section.id),
					sections_url: window.location.pathname,
				});

				fetch(url, { ...fetchConfig(), ...{ body } })
					.then((response) => response.json())
					.then((parsedState) => {
						if (parsedState.status) {
							const error = new Error(parsedState.message);
							// Only Shopify wording is worth showing, anything else stays generic.
							error.cartMessage = parsedState.description || parsedState.message;
							throw error;
						}

						this.renderSections(parsedState);

						// Same source as cart.js so the drawer does not fetch the section twice.
						publish(PUB_SUB_EVENTS.cartUpdate, {
							source: 'cart-items',
							cartData: parsedState,
							variantId: checkbox.dataset.variantId,
						});
					})
					.catch((error) => {
						console.error(error);
						checkbox.checked = !checkbox.checked;
						this.showError(error.cartMessage);
						this.setLoading(false);
					});
			}

			getSectionsToRender() {
				const sections = [
					{ id: 'cart-drawer', target: document.getElementById('CartDrawer'), selector: '#CartDrawer' },
					{ id: 'cart-icon-bubble', target: document.getElementById('cart-icon-bubble'), selector: '.shopify-section' },
				];

				// Keep the cart page itself in sync when the drawer is opened on top of it.
				const mainCartItems = document.getElementById('main-cart-items');
				const mainCartFooter = document.getElementById('main-cart-footer');

				if (mainCartItems) {
					sections.push({ id: mainCartItems.dataset.id, target: mainCartItems, selector: '.js-contents' });
				}
				if (mainCartFooter) {
					sections.push({ id: mainCartFooter.dataset.id, target: mainCartFooter, selector: '.js-contents' });
				}

				return sections.filter((section) => section.id && section.target);
			}

			renderSections(parsedState) {
				const renderedSections = parsedState.sections || {};
				const cartDrawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
				const itemCount = parsedState.item_count;

				// `/cart/add.js` answers with the added line, not with the whole cart.
				if (typeof itemCount === 'number') {
					const mainCartFooter = document.getElementById('main-cart-footer');

					if (cartDrawer) cartDrawer.classList.toggle('is-empty', itemCount === 0);
					if (mainCartFooter) mainCartFooter.classList.toggle('is-empty', itemCount === 0);
				}

				this.getSectionsToRender().forEach((section) => {
					const html = renderedSections[section.id];
					if (!html) return;

					const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
					if (!source) return;

					const elementToReplace = section.target.querySelector(section.selector) || section.target;
					elementToReplace.innerHTML = source.innerHTML;
				});

				// The overlay was rebuilt with the drawer, so its close handler is gone.
				const overlay = cartDrawer && cartDrawer.querySelector('#CartDrawer-Overlay');
				if (overlay) overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
			}

			setLoading(isLoading) {
				this.setAttribute('aria-busy', isLoading);
				this.querySelectorAll('.cart-gift-wrap__checkbox').forEach((checkbox) => {
					checkbox.disabled = isLoading;
				});
			}

			showError(message) {
				if (!this.errorElement) return;

				this.errorElement.textContent = message || window.cartStrings.error;
				this.errorElement.hidden = false;
			}

			hideError() {
				if (!this.errorElement) return;

				this.errorElement.textContent = '';
				this.errorElement.hidden = true;
			}
		}
	);
}
